import { useState, useEffect, useRef } from "react";

import {
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  PencilIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CodeBracketIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { toast } from "react-toastify";

import RichTextEditor from "../components/RichTextEditor";
import apiService from "../services/api";
import {
  FAQ_PLATFORMS,
  PLATFORM_PILL,
  categoryMatchesPlatform,
} from "../utils/faqPlatforms";

const FAQManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [faqData, setFaqData] = useState([]);

  const [newQuestion, setNewQuestion] = useState({ q: "", a: "" });
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showAddQuestion, setShowAddQuestion] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportCode, setExportCode] = useState("");
  const [exportType, setExportType] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const importInputRef = useRef(null);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await apiService.getFAQs(false);

      if (response.status === 200 && response.data) {
        // Lift each category's platform tags out of its questions' metadata
        // (we store them denormalised — every question in a category shares the
        // same list) so the UI can edit them at the category level.
        setFaqData(
          response.data.map((category) => ({
            ...category,
            platforms: category.questions?.[0]?.metadata?.platforms || [],
          }))
        );
      }
    } catch (error) {
      toast.error("Failed to Load FAQs");
    } finally {
      setLoading(false);
    }
  };

  // Save FAQ data to API
  const saveFAQ = async () => {
    try {
      setSaving(true);
      // Denormalise each category's platform tags onto every question's
      // metadata. The /bulk endpoint already round-trips question.metadata, so
      // this persists with no schema or API change. Empty list = all platforms.
      const payload = faqData.map((category) => ({
        ...category,
        questions: category.questions.map((question) => ({
          ...question,
          metadata: {
            ...(question.metadata || {}),
            platforms: category.platforms || [],
          },
        })),
      }));

      await apiService.bulkCreateFAQs(payload);
      toast.success("FAQs Updated Successfully!");
      setHasChanges(false);
      await fetchFAQs();
    } catch (error) {
      toast.error(`Failed to Save FAQs: ${error.message || "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  };

  // Add new category
  const addCategory = () => {
    if (newCategory.trim()) {
      setFaqData([
        ...faqData,
        { category: newCategory, questions: [], platforms: [] },
      ]);
      setNewCategory("");
      setShowAddCategory(false);
      setHasChanges(true);
    }
  };

  // Delete category
  const deleteCategory = (index) => {
    const updatedData = faqData.filter((_, i) => i !== index);

    setFaqData(updatedData);
    setHasChanges(true);
  };

  // Update category name
  const updateCategoryName = (index, newName) => {
    const updatedData = [...faqData];

    updatedData[index].category = newName;
    setFaqData(updatedData);
    setEditingCategory(null);
    setHasChanges(true);
  };

  // Toggle a platform tag on a category. An empty list means the category
  // shows on every platform.
  const toggleCategoryPlatform = (index, key) => {
    const updatedData = [...faqData];
    const current = updatedData[index].platforms || [];

    updatedData[index] = {
      ...updatedData[index],
      platforms: current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key],
    };
    setFaqData(updatedData);
    setHasChanges(true);
  };

  // Add question to category
  const addQuestion = (categoryIndex) => {
    if (newQuestion.q.trim() && newQuestion.a.trim()) {
      const updatedData = [...faqData];

      updatedData[categoryIndex].questions.push({ ...newQuestion });
      setFaqData(updatedData);
      setNewQuestion({ q: "", a: "" });
      setShowAddQuestion(null);
      setHasChanges(true);
    }
  };

  // Delete question
  const deleteQuestion = (categoryIndex, questionIndex) => {
    const updatedData = [...faqData];

    updatedData[categoryIndex].questions = updatedData[
      categoryIndex
    ].questions.filter((_, i) => i !== questionIndex);
    setFaqData(updatedData);
    setHasChanges(true);
  };

  // Update question
  const updateQuestion = (categoryIndex, questionIndex, field, value) => {
    const updatedData = [...faqData];

    updatedData[categoryIndex].questions[questionIndex][field] = value;
    setFaqData(updatedData);
    setHasChanges(true);
  };

  // Move category up/down
  const moveCategory = (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;

    if (newIndex >= 0 && newIndex < faqData.length) {
      const updatedData = [...faqData];

      [updatedData[index], updatedData[newIndex]] = [
        updatedData[newIndex],
        updatedData[index],
      ];
      setFaqData(updatedData);
      setHasChanges(true);
    }
  };

  // Move question up/down
  const moveQuestion = (categoryIndex, questionIndex, direction) => {
    const newIndex = direction === "up" ? questionIndex - 1 : questionIndex + 1;
    const { questions } = faqData[categoryIndex];

    if (newIndex >= 0 && newIndex < questions.length) {
      const updatedData = [...faqData];

      [questions[questionIndex], questions[newIndex]] = [
        questions[newIndex],
        questions[questionIndex],
      ];
      setFaqData(updatedData);
      setHasChanges(true);
    }
  };

  // Export functions
  const exportFAQ = (type) => {
    setShowExportMenu(false);

    if (type === "json") {
      const jsonData = JSON.stringify(faqData, null, 2);

      downloadFile(jsonData, "faq-data.json", "application/json");
    } else if (type === "html") {
      const htmlContent = generateHTML();

      setExportCode(htmlContent);
      setExportType("html");
      setShowExportModal(true);
    } else if (type === "embed") {
      const embedCode = generateEmbedCode();

      setExportCode(embedCode);
      setExportType("embed");
      setShowExportModal(true);
    }
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("File downloaded successfully");
  };

  const generateHTML = () => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>FAQ - Lunova</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; background: #f5f5f5; padding: 20px; }
    .faq-container { max-width: 800px; margin: 0 auto; background: white; border-radius: 12px; padding: 30px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
    h1 { color: #1a1a1a; margin-bottom: 30px; font-size: 2.5em; }
    .faq-category { margin-bottom: 30px; }
    .faq-category h2 { color: #2563eb; margin-bottom: 20px; font-size: 1.5em; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px; }
    .faq-item { margin-bottom: 20px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
    .faq-question { padding: 15px; background: #f9fafb; cursor: pointer; font-weight: 600; display: flex; justify-content: space-between; align-items: center; transition: background 0.3s; }
    .faq-question:hover { background: #f3f4f6; }
    .faq-answer { padding: 15px; display: none; background: white; }
    .faq-answer.active { display: block; }
    .faq-answer a { color: #2563eb; text-decoration: underline; }
    .faq-answer ul, .faq-answer ol { margin-left: 20px; margin-top: 10px; margin-bottom: 10px; }
    .faq-answer code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; }
    .chevron { transition: transform 0.3s; }
    .chevron.active { transform: rotate(180deg); }
  </style>
</head>
<body>
  <div class="faq-container">
    <h1>Frequently Asked Questions</h1>
    ${faqData
      .map(
        (category) => `
    <div class="faq-category">
      <h2>${category.category}</h2>
      ${category.questions
        .map(
          (item) => `
      <div class="faq-item">
        <div class="faq-question" onclick="toggleAnswer(this)">
          <span>${item.q}</span>
          <span class="chevron">▼</span>
        </div>
        <div class="faq-answer">${item.a}</div>
      </div>
      `
        )
        .join("")}
    </div>
    `
      )
      .join("")}
  </div>
  <script>
    function toggleAnswer(element) {
      const answer = element.nextElementSibling;
      const chevron = element.querySelector('.chevron');
      answer.classList.toggle('active');
      chevron.classList.toggle('active');
    }
  </script>
</body>
</html>`;

  const generateEmbedCode = () => `<!-- Lunova FAQ Widget -->
<div id="lunova-faq-widget"></div>
<script>
(function() {
  const faqData = ${JSON.stringify(faqData)};
  
  const styles = \`
    #lunova-faq-widget { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
    #lunova-faq-widget .faq-category { margin-bottom: 20px; }
    #lunova-faq-widget .faq-category h3 { color: #1f2937; margin-bottom: 15px; font-size: 1.25em; }
    #lunova-faq-widget .faq-item { margin-bottom: 10px; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
    #lunova-faq-widget .faq-question { padding: 12px; background: #f9fafb; cursor: pointer; font-weight: 500; }
    #lunova-faq-widget .faq-question:hover { background: #f3f4f6; }
    #lunova-faq-widget .faq-answer { padding: 12px; display: none; background: white; }
    #lunova-faq-widget .faq-answer.show { display: block; }
    #lunova-faq-widget .faq-answer a { color: #2563eb; }
  \`;
  
  const styleSheet = document.createElement("style");
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
  
  const container = document.getElementById('lunova-faq-widget');
  let html = '';
  
  faqData.forEach(category => {
    html += '<div class="faq-category"><h3>' + category.category + '</h3>';
    category.questions.forEach(item => {
      html += '<div class="faq-item">';
      html += '<div class="faq-question" onclick="this.nextElementSibling.classList.toggle(\\'show\\')">' + item.q + '</div>';
      html += '<div class="faq-answer">' + item.a + '</div>';
      html += '</div>';
    });
    html += '</div>';
  });
  
  container.innerHTML = html;
})();
</script>
<!-- End Lunova FAQ Widget -->`;

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  // Import a JSON file (array of { category, platforms?, questions: [{ q, a }] })
  // into the editor, replacing the current list. Nothing is persisted until the
  // user hits Save — this only fills the editor for review.
  const handleImportFile = (event) => {
    const file = event.target.files?.[0];

    event.target.value = "";
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);

        if (!Array.isArray(parsed)) {
          throw new Error("File must be an array of categories");
        }

        const normalized = parsed.map((category) => ({
          category: category.category || "Untitled",
          platforms: Array.isArray(category.platforms)
            ? category.platforms
            : [],
          questions: Array.isArray(category.questions)
            ? category.questions.map((question) => ({
                q: question.q || "",
                a: question.a || "",
                ...(question.metadata ? { metadata: question.metadata } : {}),
              }))
            : [],
        }));

        if (
          faqData.length > 0 &&
          !window.confirm(
            "Replace the FAQ list in the editor with the imported file? You can review everything before saving."
          )
        ) {
          return;
        }

        setFaqData(normalized);
        setHasChanges(true);
        toast.success(
          `Imported ${normalized.length} categories — review and Save`
        );
      } catch (error) {
        toast.error(`Import failed: ${error.message}`);
      }
    };

    reader.readAsText(file);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading FAQs...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            FAQ Manager
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage frequently asked questions for Lunova Player
          </p>
        </div>

        <div className="flex gap-3">
          {hasChanges && (
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded-lg"
              initial={{ opacity: 0, scale: 0.9 }}
            >
              <i className="fas fa-exclamation-triangle" />
              <span className="text-sm">Unsaved changes</span>
            </motion.div>
          )}

          <input
            accept="application/json,.json"
            className="hidden"
            ref={importInputRef}
            type="file"
            onChange={handleImportFile}
          />
          <button
            className="btn btn-secondary flex items-center gap-2"
            onClick={() => importInputRef.current?.click()}
          >
            <ArrowUpTrayIcon className="w-5 h-5" />
            Import
          </button>

          <div className="relative">
            <button
              className="btn btn-secondary flex items-center gap-2"
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Export
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg flex items-center gap-2"
                  onClick={() => exportFAQ("json")}
                >
                  <CodeBracketIcon className="w-4 h-4" />
                  Export as JSON
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  onClick={() => exportFAQ("html")}
                >
                  <DocumentTextIcon className="w-4 h-4" />
                  Export as HTML
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg flex items-center gap-2"
                  onClick={() => exportFAQ("embed")}
                >
                  <CodeBracketIcon className="w-4 h-4" />
                  Embed Code
                </button>
              </div>
            )}
          </div>

          <button
            className={`btn ${hasChanges ? "btn-primary" : "btn-disabled"} flex items-center gap-2`}
            disabled={!hasChanges || saving}
            onClick={saveFAQ}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Saving...
              </>
            ) : (
              <>
                <CheckIcon className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Platform filter */}
      <div className="flex items-center flex-wrap gap-2 mb-6">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          Platform:
        </span>
        {[{ key: "all", label: "All" }, ...FAQ_PLATFORMS].map((p) => (
          <button
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              platformFilter === p.key
                ? "bg-blue-600 text-white"
                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
            key={p.key}
            onClick={() => setPlatformFilter(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Categories */}
      <div className="space-y-6">
        {faqData.map((category, categoryIndex) =>
          categoryMatchesPlatform(category.platforms, platformFilter) ? (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
              initial={{ opacity: 0, y: 20 }}
              key={categoryIndex}
              transition={{ delay: categoryIndex * 0.1 }}
            >
              {/* Category Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {editingCategory === categoryIndex ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        className="px-3 py-1 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        type="text"
                        value={category.category}
                        onChange={(e) => {
                          const updatedData = [...faqData];

                          updatedData[categoryIndex].category = e.target.value;
                          setFaqData(updatedData);
                        }}
                      />
                      <button
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() =>
                          updateCategoryName(categoryIndex, category.category)
                        }
                      >
                        <CheckIcon className="w-5 h-5" />
                      </button>
                      <button
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        onClick={() => setEditingCategory(null)}
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {category.category}
                      </h2>
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm">
                        {category.questions.length} questions
                      </span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Move buttons */}
                  <button
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                    disabled={categoryIndex === 0}
                    onClick={() => moveCategory(categoryIndex, "up")}
                  >
                    <ChevronUpIcon className="w-4 h-4" />
                  </button>
                  <button
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                    disabled={categoryIndex === faqData.length - 1}
                    onClick={() => moveCategory(categoryIndex, "down")}
                  >
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>

                  {/* Edit button */}
                  <button
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    onClick={() => setEditingCategory(categoryIndex)}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>

                  {/* Delete button */}
                  <button
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete category "${category.category}" and all its questions?`
                        )
                      ) {
                        deleteCategory(categoryIndex);
                      }
                    }}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>

                  {/* Add question button */}
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setShowAddQuestion(categoryIndex)}
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Question
                  </button>
                </div>
              </div>

              {/* Per-category platform tags */}
              <div className="flex items-center flex-wrap gap-2 mb-4">
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
                  Shows on:
                </span>
                {FAQ_PLATFORMS.map((p) => {
                  const on = (category.platforms || []).includes(p.key);

                  return (
                    <button
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        on
                          ? PLATFORM_PILL[p.key]
                          : "bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:text-gray-600 dark:hover:text-gray-300"
                      }`}
                      key={p.key}
                      onClick={() =>
                        toggleCategoryPlatform(categoryIndex, p.key)
                      }
                    >
                      {p.label}
                    </button>
                  );
                })}
                {(category.platforms || []).length === 0 && (
                  <span className="text-xs italic text-gray-400 dark:text-gray-500">
                    All platforms
                  </span>
                )}
              </div>

              {/* Questions */}
              <div className="space-y-3">
                {category.questions.map((question, questionIndex) => (
                  <div
                    className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    key={questionIndex}
                  >
                    {editingQuestion === `${categoryIndex}-${questionIndex}` ? (
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Question
                          </label>
                          <div className="mt-1">
                            <RichTextEditor
                              placeholder="Enter the question..."
                              value={question.q}
                              onChange={(value) =>
                                updateQuestion(
                                  categoryIndex,
                                  questionIndex,
                                  "q",
                                  value
                                )
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Answer
                          </label>
                          <div className="mt-1">
                            <RichTextEditor
                              placeholder="Enter the answer..."
                              value={question.a}
                              onChange={(value) =>
                                updateQuestion(
                                  categoryIndex,
                                  questionIndex,
                                  "a",
                                  value
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setEditingQuestion(null)}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white mb-2">
                            <span className="font-semibold">Q:</span>{" "}
                            <span
                              dangerouslySetInnerHTML={{ __html: question.q }}
                            />
                          </div>
                          <div className="text-gray-600 dark:text-gray-400">
                            <span className="font-semibold">A:</span>{" "}
                            <span
                              dangerouslySetInnerHTML={{ __html: question.a }}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-50"
                            disabled={questionIndex === 0}
                            onClick={() =>
                              moveQuestion(categoryIndex, questionIndex, "up")
                            }
                          >
                            <ChevronUpIcon className="w-3 h-3" />
                          </button>
                          <button
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-50"
                            disabled={
                              questionIndex === category.questions.length - 1
                            }
                            onClick={() =>
                              moveQuestion(categoryIndex, questionIndex, "down")
                            }
                          >
                            <ChevronDownIcon className="w-3 h-3" />
                          </button>
                          <button
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                            onClick={() =>
                              setEditingQuestion(
                                `${categoryIndex}-${questionIndex}`
                              )
                            }
                          >
                            <PencilIcon className="w-3 h-3" />
                          </button>
                          <button
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded"
                            onClick={() =>
                              deleteQuestion(categoryIndex, questionIndex)
                            }
                          >
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add Question Form */}
                {showAddQuestion === categoryIndex && (
                  <motion.div
                    animate={{ opacity: 1, height: "auto" }}
                    className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-dashed border-blue-300 dark:border-blue-700"
                    initial={{ opacity: 0, height: 0 }}
                  >
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Question
                        </label>
                        <div className="mt-1">
                          <RichTextEditor
                            placeholder="Enter the question..."
                            value={newQuestion.q}
                            onChange={(value) =>
                              setNewQuestion({ ...newQuestion, q: value })
                            }
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Answer
                        </label>
                        <div className="mt-1">
                          <RichTextEditor
                            placeholder="Enter the answer..."
                            value={newQuestion.a}
                            onChange={(value) =>
                              setNewQuestion({ ...newQuestion, a: value })
                            }
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => addQuestion(categoryIndex)}
                        >
                          Add Question
                        </button>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => {
                            setShowAddQuestion(null);
                            setNewQuestion({ q: "", a: "" });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ) : null
        )}

        {/* Add Category Button */}
        {showAddCategory ? (
          <motion.div
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            initial={{ opacity: 0, scale: 0.95 }}
          >
            <div className="flex items-center gap-3">
              <input
                autoFocus
                className="flex-1 px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                placeholder="Enter category name..."
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              />
              <button className="btn btn-primary" onClick={addCategory}>
                Add Category
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowAddCategory(false);
                  setNewCategory("");
                }}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        ) : (
          <button
            className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-blue-500 dark:hover:border-blue-400 transition-colors group"
            onClick={() => setShowAddCategory(true)}
          >
            <div className="flex items-center justify-center gap-3 text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400">
              <PlusIcon className="w-6 h-6" />
              <span className="font-medium">Add New Category</span>
            </div>
          </button>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">
                  {exportType === "html" ? "HTML Export" : "Embed Code"}
                </h2>
                <button
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  onClick={() => setShowExportModal(false)}
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="mb-4">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {exportType === "html"
                    ? "Copy this complete HTML page to host your FAQ independently:"
                    : "Copy this code snippet to embed the FAQ widget on your website:"}
                </p>
                <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto">
                  <code className="text-sm font-mono text-gray-800 dark:text-gray-200">
                    {exportCode}
                  </code>
                </pre>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  const blob = new Blob([exportCode], {
                    type:
                      exportType === "html" ? "text/html" : "text/javascript",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");

                  a.href = url;
                  a.download =
                    exportType === "html" ? "faq.html" : "faq-widget.js";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success("File downloaded!");
                }}
              >
                Download File
              </button>
              <div className="flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={() => copyToClipboard(exportCode)}
                >
                  Copy to Clipboard
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowExportModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQManager;
